import React, { useState, useEffect, useRef } from "react";
import { Icons } from "../SkillsAcademy/icons.jsx";
import ActionButton from "../ActionButton/ActionButton";
import {
  OrientationBackdrop,
  OrientationPanel,
  OrientationTopBar,
  OrientationBrandMark,
  OrientationTopBarTitle,
  OrientationStepCount,
  OrientationCloseBtn,
  OrientationProgressRow,
  OrientationProgressSegment,
  OrientationProgressFill,
  OrientationVideoStage,
  OrientationVideoEl,
  OrientationBody,
  OrientationEyebrow,
  OrientationTitle,
  OrientationBlurb,
  OrientationFooter,
  OrientationFooterSpacer,
} from "./style.js";

// ── Step definitions ──────────────────────────────────────────────────────────

const STEPS = [
  {
    eyebrow: "Step 1 of 4",
    title: "Platform Overview and Navigation",
    blurb:
      "Get familiar with the GuruNow dashboard — learn where to find your programs, sessions, and simulations, and how to make the most of the platform from day one.",
    accent: ["#01ECFF", "#962EFF"],
    video: "https://gurunow-media-prod.s3.us-east-1.amazonaws.com/media/GN_+Getting+Started+(1).mp4",
  },
  {
    eyebrow: "Step 2 of 4",
    title: "Learn How to Navigate Lessons",
    blurb:
      "Each skill development session guides you through a focused concept with your Guru. Here's how to move through the lesson flow and get the most from every session.",
    accent: ["#962EFF", "#01ECFF"],
    video: "https://gurunow-media-prod.s3.us-east-1.amazonaws.com/media/How+Skill+Dev+Session+Works.mp4",
  },
  {
    eyebrow: "Step 3 of 4",
    title: "See How Feedback Helps You Improve Your Score",
    blurb:
      "After each simulation GuruNow scores your performance and breaks down exactly what went well and what to improve — so every run counts.",
    accent: ["#A87EFF", "#01ECFF"],
    video: "https://gurunow-media-prod.s3.us-east-1.amazonaws.com/media/How+Simulation+Challenges+%26+Feedback+Coach+Work.mp4",
  },
  {
    eyebrow: "Step 4 of 4",
    title: "Meet Your On-Demand Coach",
    blurb:
      "Your Guru coach is available whenever you need support. Learn how to start a coaching conversation and get personalised guidance throughout your program.",
    accent: ["#01ECFF", "#A87EFF"],
    video: "https://gurunow-media-prod.s3.us-east-1.amazonaws.com/media/Meet+your+On-Demand+Coach.mp4",
  },
];

// ── Props ─────────────────────────────────────────────────────────────────────

interface Program {
  id: string;
  title: string;
  guru?: { name: string };
  [key: string]: unknown;
}

interface Props {
  program: Program;
  onFinish: () => void;
  onSkip: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

const OrientationModal = ({ program, onFinish, onSkip }: Props) => {
  const [step, setStep] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const currentStep = STEPS[step];
  const isLast = step === STEPS.length - 1;

  // Pause and reset video whenever the step changes
  useEffect(() => {
    const vid = videoRef.current;
    if (vid) {
      vid.pause();
      vid.currentTime = 0;
    }
  }, [step]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onSkip();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onSkip]);

  const handleNext = () => {
    if (isLast) {
      onFinish();
    } else {
      setStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep((s) => s - 1);
  };

  return (
    <OrientationBackdrop onClick={onSkip}>
      <OrientationPanel onClick={(e) => e.stopPropagation()}>
        {/* Top bar */}
        <OrientationTopBar>
          <OrientationBrandMark>G</OrientationBrandMark>
          <OrientationTopBarTitle>{program.title}</OrientationTopBarTitle>
          <OrientationStepCount>
            {step + 1} / {STEPS.length}
          </OrientationStepCount>
          <OrientationCloseBtn type="button" onClick={onSkip} aria-label="Close">
            <Icons.x size={16} />
          </OrientationCloseBtn>
        </OrientationTopBar>

        {/* Progress segments */}
        <OrientationProgressRow>
          {STEPS.map((_, i) => (
            <OrientationProgressSegment
              key={i}
              type="button"
              $done={i < step}
              $active={i === step}
              onClick={() => setStep(i)}
            >
              {i === step && <OrientationProgressFill />}
            </OrientationProgressSegment>
          ))}
        </OrientationProgressRow>

        {/* Video stage */}
        <OrientationVideoStage $a={currentStep.accent[0]} $b={currentStep.accent[1]}>
          <OrientationVideoEl
            ref={videoRef}
            key={currentStep.video}
            src={`${currentStep.video}#t=0.001`}
            preload="metadata"
            controls
            playsInline
          />
        </OrientationVideoStage>

        {/* Body */}
        <OrientationBody>
          <OrientationEyebrow>{currentStep.eyebrow}</OrientationEyebrow>
          <OrientationTitle>{currentStep.title}</OrientationTitle>
          <OrientationBlurb>{currentStep.blurb}</OrientationBlurb>
        </OrientationBody>

        {/* Footer */}
        <OrientationFooter>
          <ActionButton
            variant="subtle"
            size="md"
            type="button"
            onClick={handleBack}
            disabled={step === 0}
            style={{ opacity: step === 0 ? 0.4 : 1 }}
          >
            Back
          </ActionButton>
          <OrientationFooterSpacer />
          {!isLast && (
            <ActionButton
              variant="ghost"
              size="md"
              type="button"
              onClick={onSkip}
              style={{ color: "#A87EFF", fontWeight: 400 }}
            >
              Skip — I'll figure it out
            </ActionButton>
          )}
          <ActionButton
            variant="subtle"
            size="md"
            type="button"
            onClick={handleNext}
            iconRight={<Icons.arrowRight size={16} />}
          >
            {isLast ? "Start" : "Next"}
          </ActionButton>
        </OrientationFooter>
      </OrientationPanel>
    </OrientationBackdrop>
  );
};

export default OrientationModal;
